import React, { useMemo, useState } from "react";
import { CalendarClock, Search, Compass, Info } from "lucide-react";
import { PageHeader } from "@/components/shell/TopBar";
import { PrematchCard } from "@/components/cards/PrematchCard";
import { LoadingCards, ErrorState, EmptyState } from "@/components/common/States";
import { fmtNum } from "@/lib/format";
import { usePrematch, useMarketHub } from "@/hooks/useAtlas";
import { marketHubRowToFixture } from "@/api/marketHub";
import { resolveMarket, CATEGORY_LABEL, MarketCategory } from "@/presentation/marketRegistry";
import { marketTypeRisk, RiskLevel, RISK_LEVELS, riskLevelLabel } from "@/presentation/riskModel";
import { lifecycleOf } from "@/presentation/resultPresentation";
import { normalizeCompetition } from "@/presentation/competitionNormalizer";
import { PrematchFixture } from "@/types/api";
import { useSession } from "@/context/SessionContext";
import { useI18n } from "@/i18n";

type Tab = "EXPLORE" | "PICKS" | "RESOLVED";
type Window = "ALL" | "TODAY" | "TOMORROW" | "NEXT24";

function inWindow(iso: string | null, w: Window): boolean {
  if (w === "ALL") return true;
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return false;
  const now = Date.now();
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const dayMs = 86400000;
  if (w === "NEXT24") return t >= now && t <= now + dayMs;
  const dayIdx = Math.floor((t - startOfToday.getTime()) / dayMs);
  if (w === "TODAY") return dayIdx === 0;
  if (w === "TOMORROW") return dayIdx === 1;
  return true;
}

export default function Prematch() {
  const { d, lang } = useI18n();
  const { isAuthenticated } = useSession();
  const legacyQ = usePrematch(120, isAuthenticated);
  const mh = useMarketHub();
  const mhView = mh.data;
  const [tab, setTab] = useState<Tab>("EXPLORE");
  const [win, setWin] = useState<Window>("ALL");
  const [cat, setCat] = useState<string>("ALL");
  const [risk, setRisk] = useState<string>("ALL");
  const [country, setCountry] = useState<string>("ALL");
  const [q, setQ] = useState("");

  const legacyRows = legacyQ.data?.rows || [];
  // CANONICAL INVENTORY: Market Hub prematch (FootyStats-aligned, same source the
  // Telegram publisher now uses) is the render list owner whenever the
  // authenticated Market Hub is available. A canonical fixture renders even with
  // NO legacy counterpart; legacy only ENRICHES by fixture_id (extra markets,
  // resolved history). Legacy is a FULL fallback ONLY when Market Hub is
  // genuinely unavailable (not authenticated, request error, or no data).
  const marketHubAvailable = isAuthenticated && !mh.isError && !!mhView;
  const rows = useMemo(() => {
    if (!marketHubAvailable) return legacyRows;
    const legacyById = new Map<string, PrematchFixture>(legacyRows.map((r) => [String(r.fixture_id), r]));
    const canonical = (mhView!.prematch || []).map((hub) => marketHubRowToFixture(hub, legacyById.get(String(hub.fixture_id))));
    const canonicalIds = new Set(canonical.map((r) => String(r.fixture_id)));
    // Preserve legacy RESOLVED history for the Resolved tab (Market Hub prematch
    // is forward-looking); never let legacy inject into the upcoming inventory.
    const resolvedLegacy = legacyRows.filter((r) => !canonicalIds.has(String(r.fixture_id)) && lifecycleOf(r.status, r.won) !== "PENDING");
    return [...canonical, ...resolvedLegacy];
  }, [marketHubAvailable, mhView, legacyRows]);

  const mhPending = isAuthenticated && mh.isLoading && !mhView && !mh.isError;
  const isLoading = mhPending || legacyQ.isLoading;
  const isError = marketHubAvailable ? false : legacyQ.isError;
  const error = legacyQ.error;
  const refetch = () => { legacyQ.refetch(); mh.refetch(); };
  const stats = useMemo(() => ({
    scanned: rows.length,
    candidates: rows.filter((f) => (f.markets?.length || 0) > 0).length,
    published: rows.filter((f) => lifecycleOf(f.status, f.won) === "PENDING").length,
  }), [rows]);

  const countries = useMemo(() => Array.from(new Set(rows.map((f) => normalizeCompetition(f.country, f.league).country).filter(Boolean))).sort(), [rows]);
  const cats = useMemo(() => {
    const set = new Set<MarketCategory>();
    rows.forEach((f) => f.markets?.forEach((m) => set.add(resolveMarket(m.market_code, lang).category)));
    return Array.from(set);
  }, [rows, lang]);

  const bestRisk = (f: PrematchFixture): RiskLevel | null => {
    const b = f.best || f.markets?.[0];
    return b ? (marketTypeRisk(b.market_code, lang).level as RiskLevel) : null;
  };

  const filtered = useMemo(() => {
    return rows.filter((f) => {
      const key = lifecycleOf(f.status, f.won);
      if (tab === "PICKS" && key !== "PENDING") return false;
      if (tab === "RESOLVED" && key === "PENDING") return false;
      if (tab === "EXPLORE" && key !== "PENDING") return false;
      if (tab !== "RESOLVED" && !inWindow(f.fixture_date, win)) return false;
      if (cat !== "ALL") {
        const has = (f.markets || []).some((m) => resolveMarket(m.market_code, lang).category === cat);
        if (!has) return false;
      }
      if (risk !== "ALL" && bestRisk(f) !== risk) return false;
      if (country !== "ALL" && normalizeCompetition(f.country, f.league).country !== country) return false;
      if (q) {
        const hay = `${f.home_team} ${f.away_team} ${normalizeCompetition(f.country, f.league).display}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [rows, tab, win, cat, risk, country, q, lang]);

  const tabs: { k: Tab; label: string; icon: React.ReactNode }[] = [
    { k: "EXPLORE", label: d.explorer.exploreTab, icon: <Compass className="h-4 w-4" /> },
    { k: "PICKS", label: d.explorer.picksTab, icon: <CalendarClock className="h-4 w-4" /> },
    { k: "RESOLVED", label: d.explorer.resolvedTab, icon: <CalendarClock className="h-4 w-4" /> },
  ];
  const windows: { k: Window; label: string }[] = [
    { k: "ALL", label: d.explorer.all }, { k: "TODAY", label: d.explorer.today },
    { k: "TOMORROW", label: d.explorer.tomorrow }, { k: "NEXT24", label: d.explorer.next24 },
  ];

  return (
    <div data-testid="prematch-page">
      <PageHeader title={lang === "tr" ? "Maç Öncesi" : "Prematch"}
        subtitle={lang === "tr" ? "Yaklaşan maçları keşfet, ATLAS seçimlerini incele." : "Explore upcoming matches and review ATLAS picks."} />

      {!isLoading && !isError && (
        rows.length > 0 ? (
          <div className="mb-5 grid gap-3 sm:grid-cols-3" data-testid="prematch-stats">
            {[[stats.scanned, lang === "tr" ? "Taranan Maç" : "Scanned Matches"], [stats.candidates, lang === "tr" ? "Aday" : "Candidates"], [stats.published, lang === "tr" ? "Yayınlanan" : "Published"]].map(([v, l]) => (
              <div key={l as string} className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
                <p className="num text-lg font-bold text-foreground">{fmtNum(v as number)}</p>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{l as string}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="mb-5 flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.02] p-4" data-testid="prematch-stats-empty">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">{lang === "tr" ? "ATLAS uygun yayın kriterini geçen seçimleri burada gösterir." : "ATLAS shows selections that pass its publishing criteria here."}</p>
          </div>
        )
      )}

      <div className="mb-5 flex w-fit max-w-full gap-1 overflow-x-auto rounded-xl border border-white/10 bg-white/[0.03] p-1" data-testid="prematch-tabs">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`prematch-tab-${t.k.toLowerCase()}`}
            className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-semibold transition ${tab === t.k ? "bg-atlas/15 text-atlas-300" : "text-muted-foreground hover:text-foreground"}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {tab === "EXPLORE" && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.02] p-4" data-testid="explorer-note">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-xs text-muted-foreground">{lang === "tr" ? "Şu anda ATLAS'ın değerlendirdiği yaklaşan maçlar gösteriliyor. Yeni değerlendirmeler oluştukça burada görünür." : "Showing the upcoming matches ATLAS is evaluating. New evaluations appear here as they are created."}</p>
        </div>
      )}

      {/* filters */}
      <div className="mb-6 space-y-3">
        {tab !== "RESOLVED" && (
          <div className="flex flex-wrap gap-2" data-testid="prematch-windows">
            {windows.map((w) => (
              <button key={w.k} onClick={() => setWin(w.k)} data-testid={`pm-win-${w.k.toLowerCase()}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${win === w.k ? "bg-atlas/15 text-atlas-300" : "border border-white/10 text-muted-foreground hover:text-foreground"}`}>{w.label}</button>
            ))}
          </div>
        )}
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="prematch-cats">
            <button onClick={() => setCat("ALL")} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${cat === "ALL" ? "bg-atlas/15 text-atlas-300" : "border border-white/10 text-muted-foreground hover:text-foreground"}`}>{d.common.all}</button>
            {cats.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`prematch-cat-${c}`}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${cat === c ? "bg-atlas/15 text-atlas-300" : "border border-white/10 text-muted-foreground hover:text-foreground"}`}>{CATEGORY_LABEL[c][lang]}</button>
            ))}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select value={risk} onChange={(e) => setRisk(e.target.value)} data-testid="pm-risk-filter" className="rounded-xl border border-white/10 bg-navy-800 px-3 py-2 text-sm text-foreground outline-none focus:border-atlas/40">
              <option value="ALL">{d.explorer.riskLevel}: {d.common.all}</option>
              {RISK_LEVELS.map((r) => <option key={r} value={r}>{riskLevelLabel(r, lang)}</option>)}
            </select>
            <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="pm-country-filter" className="max-w-[160px] rounded-xl border border-white/10 bg-navy-800 px-3 py-2 text-sm text-foreground outline-none focus:border-atlas/40">
              <option value="ALL">{lang === "tr" ? "Ülke: Tümü" : "Country: All"}</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="relative lg:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "tr" ? "Takım / lig ara…" : "Search team / league…"} data-testid="prematch-search"
            className="w-full rounded-xl border border-white/10 bg-white/[0.03] py-2.5 pl-9 pr-3 text-sm text-foreground outline-none focus:border-atlas/40" />
        </div>
      </div>

      {isLoading ? <LoadingCards count={6} /> : isError ? <ErrorState error={error as any} onRetry={refetch} />
        : filtered.length === 0 ? <EmptyState title={tab === "EXPLORE" ? d.explorer.emptyExplorer : tab === "RESOLVED" ? d.states.emptyHistory : d.states.emptyPrematch} hint={tab === "RESOLVED" ? undefined : (lang === "tr" ? "Yeni değerlendirmeler oluştuğunda burada görünür." : "New evaluations will appear here as they are created.")} icon={<CalendarClock className="h-6 w-6" />} />
        : <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="prematch-grid">{filtered.map((f, i) => <PrematchCard key={f.fixture_key} f={f} index={i} />)}</div>}
    </div>
  );
}
